import { useHotkeys } from 'react-hotkeys-hook';
import { useModeAware } from '@features/ModeSystem/hooks/useModeAware';

interface UseGlobalShortcutsProps {
  onTabChange: (tabId: string) => void;
  onGenerate: () => void;
  isGenerating: boolean;
  activeTab: string;
  isAnyModalOpen: boolean;
  onToggleSettings: () => void;
  onToggleLibrary: () => void;
  onToggleSessionMemory: () => void;
  onToggleShortcutRef: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
}

export function useGlobalShortcuts({
  onTabChange,
  onGenerate,
  isGenerating,
  activeTab,
  isAnyModalOpen,
  onToggleSettings,
  onToggleLibrary,
  onToggleSessionMemory,
  onToggleShortcutRef,
  onUndo,
  onRedo,
}: UseGlobalShortcutsProps) {
  const { availableTabs } = useModeAware();
  const visibleTabs = availableTabs.filter(t => t.id !== 'output');

  // Alt+1-9: Switch to tab by position
  useHotkeys(
    'alt+1,alt+2,alt+3,alt+4,alt+5,alt+6,alt+7,alt+8,alt+9',
    (e) => {
      if (isAnyModalOpen) return;
      e.preventDefault();
      const digit = parseInt(e.key, 10);
      const index = digit - 1;
      if (index < visibleTabs.length) {
        onTabChange(visibleTabs[index].id);
      }
    },
    { enableOnFormTags: false },
    [visibleTabs, isAnyModalOpen, onTabChange]
  );

  // Alt+0: Jump to Output tab
  useHotkeys(
    'alt+0',
    (e) => {
      if (isAnyModalOpen) return;
      e.preventDefault();
      onTabChange('output');
    },
    { enableOnFormTags: false },
    [isAnyModalOpen, onTabChange]
  );

  // Ctrl+Enter: Generate (Studio tab only)
  useHotkeys(
    'ctrl+enter',
    (e) => {
      if (isAnyModalOpen || activeTab !== 'studio' || isGenerating) return;
      e.preventDefault();
      onGenerate();
    },
    { enableOnFormTags: false },
    [isAnyModalOpen, activeTab, isGenerating, onGenerate]
  );

  // Ctrl+,: Toggle Settings
  useHotkeys(
    'ctrl+comma',
    (e) => {
      e.preventDefault();
      onToggleSettings();
    },
    { enableOnFormTags: true },
    [onToggleSettings]
  );

  // Ctrl+Shift+L: Toggle Template Library
  useHotkeys(
    'ctrl+shift+l',
    (e) => {
      if (isAnyModalOpen) return;
      e.preventDefault();
      onToggleLibrary();
    },
    { enableOnFormTags: true },
    [isAnyModalOpen, onToggleLibrary]
  );

  // Ctrl+Shift+M: Toggle Session Memory
  useHotkeys(
    'ctrl+shift+m',
    (e) => {
      if (isAnyModalOpen) return;
      e.preventDefault();
      onToggleSessionMemory();
    },
    { enableOnFormTags: true },
    [isAnyModalOpen, onToggleSessionMemory]
  );

  // Ctrl+/: Toggle Shortcut Reference
  useHotkeys(
    'ctrl+/',
    (e) => {
      e.preventDefault();
      onToggleShortcutRef();
    },
    { enableOnFormTags: true },
    [onToggleShortcutRef]
  );

  // Ctrl+Z: Undo
  useHotkeys(
    'ctrl+z',
    (e) => {
      if (isAnyModalOpen) return;
      e.preventDefault();
      onUndo?.();
    },
    { enableOnFormTags: false },
    [isAnyModalOpen, onUndo]
  );

  // Ctrl+Shift+Z: Redo
  useHotkeys(
    'ctrl+shift+z',
    (e) => {
      if (isAnyModalOpen) return;
      e.preventDefault();
      onRedo?.();
    },
    { enableOnFormTags: false },
    [isAnyModalOpen, onRedo]
  );
}
